import {
  Badge,
  Button,
  Col,
  Dropdown,
  Image,
  Input,
  Menu,
  Drawer,
  Row,
  Skeleton,
  Tooltip,
} from "antd";

import { routes } from "../../Routes/Routes";
import { buildCategoryPath } from "../../utils/categoryUrl";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HeartOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  OrderedListOutlined,
  MenuOutlined,
  CloseOutlined,
  SettingOutlined,
  CalculatorOutlined,
  HomeOutlined,
  UserOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./index.module.scss";
import { useBranding } from "../../Context/BrandingContext";

import {
  getProfileApi,
  logout,
  setCartCount,
  setLikeCount,
} from "../../Features/User/User";

import user from "../../Assets/PNG/profile.png";

import { getCategoryApi } from "../../Features/Category/Category";
import { getSearchProductApi } from "../../Features/Product/Product";
import { getWishListApi } from "../../Features/WishList/WishList";
import { geCartListApi } from "../../Features/AddCart/AddCart";
import { ProductCard } from "../productCard";
import RingSizeChartModal from "../RingSizeChartModal/RingSizeChartModal";
import { apiUrl, BASE_URL } from "../../Constant";
import Marquee from "react-fast-marquee";
import axios from "axios";

// Must match the constant in the Catagory page — same route, same name.
// Kept below the imports: sitting between them tripped `import/first`, which
// react-app treats as an error, so the whole production build refused to compile.
const NEW_ARRIVALS_SLUG = "new-arrivals";

// Search-as-you-type used to fire on EVERY keystroke from 2 characters. Broad
// queries are far slower than precise ones — "be" takes ~17s and returns 91
// products where "belgrave" takes ~3s and returns 1 — so the vague early request
// landed last and overwrote the answer the user actually wanted. Waiting for a
// pause means the broad query is usually never sent at all; the thunk also
// discards any reply that a newer search has superseded.
// (Below the imports for the same `import/first` reason as the constant above.)
let searchDebounceTimer = null;

function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchType, setSearchType] = useState();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [ringChartOpen, setRingChartOpen] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);
  const [mobileProductRangeOpen, setMobileProductRangeOpen] = useState(false);
  const [mobileBrandedOpen, setMobileBrandedOpen] = useState(false);
  const [mobileWatchesOpen, setMobileWatchesOpen] = useState(false);
  const [showProductRangeMenu, setShowProductRangeMenu] = useState(true);
  const [showBrandedMenu, setShowBrandedMenu] = useState(true);
  const [showWatchesMenu, setShowWatchesMenu] = useState(false);
  const [hiddenBrands, setHiddenBrands] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [hiddenSubMenus, setHiddenSubMenus] = useState([]);
  const [menusLoaded, setMenusLoaded] = useState(false);
  const [menuData, setMenuData] = useState({ pr: null, br: null, w: null });
  // Controls which desktop mega menu is open. Hover trigger flips it via
  // onOpenChange; clicking any L1/L2/L3 action closes it explicitly so
  // the dropdown doesn't linger over the page after navigation.
  const [openMenu, setOpenMenu] = useState(null);
  const closeMegaMenu = () => setOpenMenu(null);

  const userId = useSelector((state) => state.user?.userId);
  const token = useSelector((state) => state.user?.token);
  const branding = useBranding();

  // Fetch retailer visibility settings
  useEffect(() => {
    if (token) {
      const fetchVisibility = async () => {
        try {
          const res = await axios.get(`${apiUrl.GET_METAL_PRICES.replace("/metal/public/prices", "/user/my-settings")}`, { headers: { Authorization: token } });
          const settings = res?.data?.settings;
          if (settings) {
            setShowProductRangeMenu(settings.Show_Product_Range_Menu !== false);
            setShowBrandedMenu(settings.Show_Branded_Menu !== false);
            setShowWatchesMenu(settings.Show_Watches_Menu === true);
            setHiddenBrands(Array.isArray(settings.Hidden_Brands) ? settings.Hidden_Brands : []);
            setHiddenCategories(Array.isArray(settings.Hidden_Categories) ? settings.Hidden_Categories : []);
            setHiddenSubMenus(Array.isArray(settings.Hidden_SubMenus) ? settings.Hidden_SubMenus : []);
            localStorage.setItem("tdc_retailer_settings", JSON.stringify(settings));
          }
        } catch (e) { /* ignore */ }
      };
      fetchVisibility();
    }
  }, [token]);

  // Filter mega-menu columns against retailer visibility settings
  // Hides L2 AND L3 items found in Hidden_SubMenus. Columns with a hidden
  // title are removed entirely.
  const filterColumns = (columns, { hideList1 = [], hideList2 = [] } = {}) => {
    const hideL1 = new Set([...hideList1, ...hideList2].map(s => (s || "").toLowerCase()));
    const hideSubs = new Set(hiddenSubMenus.map(s => (s || "").toLowerCase()));
    const isHidden = (lbl) => hideSubs.has((lbl || "").toLowerCase());
    return columns
      .filter(col => !hideL1.has((col.title || "").toLowerCase()))
      .map(col => ({
        ...col,
        items: (col.items || [])
          .filter(it => {
            const lbl = typeof it === "string" ? it : it.label;
            return !isHidden(lbl);
          })
          .map(it => {
            if (typeof it === "string") return it;
            return { ...it, children: (it.children || []).filter(c => !isHidden(c.label)) };
          }),
      }));
  };

  // Fetch menus from API — single source of truth
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const [prRes, brRes, wRes] = await Promise.all([
          axios.get(`${BASE_URL}/menu/product_range`).catch(() => null),
          axios.get(`${BASE_URL}/menu/branded_jewellery`).catch(() => null),
          axios.get(`${BASE_URL}/menu/watches`).catch(() => null),
        ]);
        const pr = prRes?.data?.data?.length > 0 ? prRes.data.data : null;
        const br = brRes?.data?.data?.length > 0 ? brRes.data.data : null;
        const w = wRes?.data?.data?.length > 0 ? wRes.data.data : null;
        if (pr) localStorage.setItem("tdc_product_range_menu", JSON.stringify(pr));
        if (br) localStorage.setItem("tdc_branded_jewellery_menu", JSON.stringify(br));
        if (w) localStorage.setItem("tdc_watches_menu", JSON.stringify(w));
        setMenuData({ pr, br, w });
        setMenusLoaded(true);
      } catch { setMenusLoaded(true); }
    };
    fetchMenus();
  }, []);

  const cartCounter = useSelector((state) => state.user?.cartCount);
  const likeCounter = useSelector((state) => state.user?.likeCount);
  const profileData = useSelector((state) => state.user?.profileData);
  const userToken = useSelector((state) => state.user?.token);
  const cartlist = useSelector((state) => state.addCart?.cartListData);
  const wishlist = useSelector((state) => state.wishList?.wishlist2);

  useEffect(() => {
    dispatch(getCategoryApi());
    dispatch(getProfileApi(userToken));
    // No search is fired here any more. This used to run on EVERY page load with
    // `searchType` still undefined, so every visit to every page sent
    // `search/get?query=undefined` — and that endpoint takes three to four
    // seconds. It also passed no success callback, which the thunk calls
    // unconditionally, so it threw inside the fetch on the way back.
    // It existed so that a cold visit to /search had something in Redux to show;
    // the search page now fetches that for itself, which is where it belongs.
    dispatch(getWishListApi(userToken));
    dispatch(geCartListApi(userToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catagoryItem = useSelector(
    (state) => state.category?.headerCategoryData,
  );
  const categoryLoading = useSelector(
    (state) => state.category?.categoryLoading,
  );

  const sortedCategories = useMemo(() => {
    if (!Array.isArray(catagoryItem)) return [];

    return [...catagoryItem].sort((a, b) => {
      const aHasChildren = a?.children?.length ? 1 : 0;
      const bHasChildren = b?.children?.length ? 1 : 0;

      return bHasChildren - aHasChildren;
    });
  }, [catagoryItem]);

  // 🔹 helper function (ADD THIS ABOVE megaMenu)
  const sortByChildrenFirst = (list = []) => {
    if (!Array.isArray(list)) return [];

    return [...list].sort((a, b) => {
      const aHasChildren = a?.children?.length ? 1 : 0;
      const bHasChildren = b?.children?.length ? 1 : 0;
      return aHasChildren - bHasChildren;
    });
  };

  const allProducts = useSelector((state) => state.product?.productAllData);
  const bestSellingProducts = allProducts?.slice(0, 2);

  const Catagory = (item) => {
    if (item == "all") {
      navigate(`/product/all`, {
        state: {
          item: item,
          id: null,
        },
      });

      return;
    }

    navigate(buildCategoryPath({ menuLevel1: item?.Category_Name }), {
      state: {
        item: item?.Category_Name,
        id: item?._id,
      },
    });
  };

  const handlesubmit = () => {
    navigate(routes.homepageUrl);
  };

  const handleCart = () => {
    // if (cartCounter > 0) {
    if (userToken) {
      navigate(routes.shoppingUrl);
    }
    //
    // }
  };

  const Like = () => {
    // if (likeCounter > 0) {
    if (userToken) {
      navigate(routes.likeUrl);
    }
    // }
  };

  const handleReset = () => {
    dispatch(logout());
    navigate(routes.userUrl);
    dispatch(setLikeCount(0));
    dispatch(setCartCount(0));
  };

  const openMobileMenu = () => setMobileMenuOpen(true);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Accordion behaviour — expanding one top-level menu collapses the rest.
  const toggleMobileTop = (which) => {
    setMobileProductRangeOpen(which === "pr" ? (v) => !v : false);
    setMobileBrandedOpen(which === "branded" ? (v) => !v : false);
    setMobileWatchesOpen(which === "watches" ? (v) => !v : false);
    setExpandedMobileSection(null);
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;
    // iOS Safari ignores overflow:hidden for touch scroll — pin the body
    // with position:fixed and restore the scroll offset on close.
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      window.scrollTo(0, scrollY);
    };
  }, [mobileMenuOpen]);

  const handleMobileNav = (callback) => {
    callback();
    closeMobileMenu();
  };

  /**
   * Build the "show me the results" callback for a search that is about to be
   * sent.
   *
   * /search/get takes three to four seconds, so by the time a reply lands the
   * customer has usually moved on — most often by clicking one of the results
   * that appeared from an earlier keystroke. Navigating to /search on that late
   * reply threw them straight back off the product page they had just opened,
   * about a third of a second after it rendered. Capture where they were when
   * the search left, and stay quiet if they are no longer there.
   */
  const goToSearchResults = ({ closeMobileSearch = false } = {}) => {
    const from = window.location.pathname;
    return () => {
      if (window.location.pathname !== from) return; // they have opened something
      if (closeMobileSearch) setMobileSearchOpen(false);
      // Already on the results page: the list re-renders from the store on its
      // own, and pushing again just stacks up history entries to back out of.
      if (from !== routes.search) navigate(routes.search);
    };
  };

  // Currently unreferenced — kept because it is the shape a search box wants,
  // but routed through goToSearchResults so that wiring it up cannot bring the
  // navigate-after-the-customer-left bug back with it.
  const handleInputChange = (e) => {
    setSearchType(e);
    if (!e || !e.trim()) return;
    localStorage.setItem("tdc_last_search", e);
    dispatch(getSearchProductApi(e, userId ? userId : 0,
      goToSearchResults({ closeMobileSearch: true })));
  };

  const mobileDropDownMenu = (
    <Menu
      items={[
        {
          label: (
            <>
              {token ? (
                <>
                  <p
                    style={{ fontSize: "16px", fontWeight: "700", margin: "0" }}
                  >
                    Hello {profileData?.User_Name}
                  </p>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      margin: "0",
                      marginBottom: "10px",
                    }}
                  >
                    {profileData?.User_Mobile_No}
                  </p>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      margin: "0",
                      marginBottom: "10px",
                      background: "#DAA520",
                      borderRadius: "10px",
                      textAlign: "center",
                      width: "50%",
                    }}
                  >
                    {profileData?.User_Type === "1"
                      ? "Gold"
                      : profileData?.User_Type === "2"
                        ? "Silver"
                        : profileData?.User_Type === "3"
                          ? "PPO"
                          : ""}
                  </p>

                  <div>
                    <Button
                      style={{
                        border: "1px solid gainsboro",
                        borderRadius: "0",
                        color: "purple",
                        width: "100%",
                        height: "45px",
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                      onClick={() => {
                        navigate(routes.signupUrl, {
                          state: {
                            update: true,
                          },
                        });
                      }}
                    >
                      UPDATE PROFILE
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: "18px", fontWeight: "700" }}>Welcome</p>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginTop: "-20px",
                    }}
                  >
                    To access account and manage orders
                  </p>
                  <div>
                    <Button
                      style={{
                        border: "1px solid gainsboro",
                        borderRadius: "0",
                        color: "red",
                        width: "70%",
                        height: "40px",
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                      onClick={() => {
                        navigate(routes.userUrl);
                      }}
                    >
                      LOGIN / SIGNUP
                    </Button>
                  </div>
                </>
              )}
              <div
                style={{
                  border: "1px solid gainsboro",
                  marginTop: "20px",
                  padding: "0",
                }}
              ></div>
            </>
          ),
          key: "0",
        },
        {
          label: (
            <>
              {token ? (
                <div
                  onClick={() => {
                    navigate(routes.accountUrl);
                  }}
                  style={{ fontWeight: "600", fontSize: "16px", cursor: "pointer" }}
                >
                  My Account
                </div>
              ) : (
                <div>Account</div>
              )}
            </>
          ),
          key: "1",
        },
        {
          label: (
            <>
              {token ? (
                <div
                  onClick={() => {
                    navigate(routes.addressUrl);
                  }}
                  style={{ fontWeight: "600", fontSize: "16px" }}
                >
                  My Address
                </div>
              ) : (
                <div>My Address</div>
              )}
            </>
          ),
          key: "4",
        },
        {
          label: (
            <>
              {userId ? (
                <div
                  onClick={() => {
                    handleReset();
                  }}
                  style={{ fontWeight: "600", fontSize: "16px" }}
                >
                  Logout
                </div>
              ) : (
                ""
              )}
            </>
          ),
          key: "3",
        },
      ]}
    />
  );

  // Load menu from admin Menu Builder (localStorage) or use defaults
  const navigateToCategory = (level1, level2, menuGroup, level3 = null) => {
    closeMegaMenu();
    // "New Arrivals" sits in the menu next to real categories, but nothing in
    // the catalogue is tagged with it — sent down the normal path it filters on
    // a category that does not exist and lands on an empty page. It is a view of
    // the newest pieces, so it gets its own route.
    const leaf = level3 || level2 || level1;
    if (/^news*arrivals?$/i.test(String(leaf || "").trim())) {
      navigate(`/product/${NEW_ARRIVALS_SLUG}`, {
        state: { item: "New Arrivals", shopAll: true, newArrivals: true },
      });
      return;
    }
    navigate(
      buildCategoryPath({ menuLevel1: level1, menuLevel2: level2, menuLevel3: level3 }),
      {
        state: {
          menuLevel1: level1,
          menuLevel2: level2 || null,
          menuLevel3: level3 || null,
          menuGroup: menuGroup || null,
        },
      },
    );
  };

  // Belt-and-braces: close any open mega menu whenever the route changes,
  // even for nav paths that don't go through navigateToCategory.
  useEffect(() => { closeMegaMenu(); }, [location.pathname, location.search]); // eslint-disable-line

  const parseMenu = (data, lsKey) => {
    if (data) return data;
    try { const s = localStorage.getItem(lsKey); return s ? JSON.parse(s) : null; } catch { return null; }
  };

  const megaMenuColumns = useMemo(() => {
    const parsed = parseMenu(menuData.pr, "tdc_product_range_menu");
    if (parsed) {
      return parsed.map(item => ({
        title: item.label,
        items: (item.children || []).map(child => ({
          label: child.label,
          action: () => navigateToCategory(item.label, child.label, "product_range"),
          children: (child.children || []).map(grand => ({
            label: grand.label,
            action: () => navigateToCategory(item.label, child.label, "product_range", grand.label),
          })),
        })),
        onTitleClick: () => navigateToCategory(item.label, null, "product_range"),
      }));
    }
    return [
      { title: "Rings", items: [], onTitleClick: () => navigateToCategory("Rings", null, "product_range") },
      { title: "Necklaces", items: [], onTitleClick: () => navigateToCategory("Necklaces", null, "product_range") },
      { title: "Bracelets", items: [], onTitleClick: () => navigateToCategory("Bracelets", null, "product_range") },
      { title: "Earrings", items: [], onTitleClick: () => navigateToCategory("Earrings", null, "product_range") },
    ];
  }, [menuData.pr]); // eslint-disable-line

  const brandedMenuColumns = useMemo(() => {
    const parsed = parseMenu(menuData.br, "tdc_branded_jewellery_menu");
    if (parsed) {
      return parsed.map(item => ({
        title: item.label,
        items: (item.children || []).map(child => ({
          label: child.label,
          action: () => navigate(buildCategoryPath({ brandName: item.label, menuLevel1: child.label }), { state: { brandName: item.label, menuLevel1: child.label, menuGroup: "branded_jewellery" } }),
          children: (child.children || []).map(grand => ({
            label: grand.label,
            action: () => navigate(buildCategoryPath({ brandName: item.label, menuLevel1: child.label, menuLevel2: grand.label }), { state: { brandName: item.label, menuLevel1: child.label, menuLevel2: grand.label, menuGroup: "branded_jewellery" } }),
          })),
        })),
        onTitleClick: () => navigate(buildCategoryPath({ brandName: item.label }), { state: { brandName: item.label, menuGroup: "branded_jewellery" } }),
      }));
    }
    return [];
  }, [menuData.br]); // eslint-disable-line

  const watchesMenuColumns = useMemo(() => {
    const parsed = parseMenu(menuData.w, "tdc_watches_menu");
    if (parsed) {
      return parsed.map(item => ({
        title: item.label,
        items: (item.children || []).map(child => ({
          label: child.label,
          action: () => navigateToCategory(item.label, child.label, "watches"),
          children: (child.children || []).map(grand => ({
            label: grand.label,
            action: () => navigateToCategory(item.label, child.label, "watches", grand.label),
          })),
        })),
        onTitleClick: () => navigateToCategory(item.label, null, "watches"),
      }));
    }
    return [];
  }, [menuData.w]); // eslint-disable-line

  // Per-retailer filtered columns (apply on every render — filterColumns is cheap)
  const visibleProductRange = filterColumns(megaMenuColumns, { hideList1: hiddenCategories });
  const visibleBranded = filterColumns(brandedMenuColumns, { hideList1: hiddenBrands });
  const visibleWatches = filterColumns(watchesMenuColumns, { hideList1: hiddenBrands });
  const watchesMenuReady = showWatchesMenu && visibleWatches.length > 0;

  // One mega-menu column: L1 heading, its L2 items with any L3 children
  // indented under them, and a "View All" into the whole category.
  //
  // Shared by BOTH desktop menus on purpose. Product Range and Branded/Watches
  // rendered byte-identical copies of this markup, which is how the two drifted
  // — Product Range grew a bottom bar the others never got. One copy now, so a
  // change to a column lands everywhere at once.
  const renderMegaColumn = (col) => (
    <div className={styles.megaColumn} key={col.title}>
      <h4 className={styles.megaTitle} onClick={col.onTitleClick} style={{ cursor: "pointer" }}>{col.title}</h4>
      <div className={styles.megaTitleLine} />
      {col.items.map((item, i) => {
        const label = typeof item === "string" ? item : item.label;
        const action = typeof item === "object" ? item.action : null;
        const grandKids = typeof item === "object" && Array.isArray(item.children) ? item.children : [];
        return (
          <div key={i}>
            <p className={styles.megaItem} onClick={action || undefined} style={{ cursor: action ? "pointer" : "default" }}>
              {label}
            </p>
            {grandKids.length > 0 && (
              <div className={styles.megaL3Group}>
                {grandKids.map((g, gi) => (
                  <p key={gi} className={styles.megaL3Item} onClick={g.action || undefined}>
                    {g.label}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {/* Desktop parity with the mobile drawer, which has offered this per
          category for a while. Reuses the heading's own handler, so it lands on
          exactly the page clicking the heading does. */}
      {col.onTitleClick && (
        <span className={styles.megaColumnViewAll} onClick={col.onTitleClick}>
          View All {col.title}
        </span>
      )}
    </div>
  );

  const buildMegaMenu = (columns) => ({
    items: [
      {
        key: "menu",
        label: (
          <div className={styles.megaMenu} onMouseLeave={closeMegaMenu}>
            <div className={styles.megaInner}>
              <div className={styles.megaCategories}>
                {columns.map(renderMegaColumn)}
              </div>
            </div>
          </div>
        ),
      },
    ],
  });

  // Product Range mega menu — same columns as buildMegaMenu (Branded, Watches)
  // via renderMegaColumn, plus the "View All Products" bottom bar that's unique
  // to Product Range.
  const megaMenu = (category) => ({
    items: [
      {
        key: category._id,
        label: (
          <div className={styles.megaMenu} onMouseLeave={closeMegaMenu}>
            <div className={styles.megaInner}>
              <div className={styles.megaCategories}>
                {visibleProductRange.map(renderMegaColumn)}
              </div>
            </div>

            <div className={styles.megaBottomBar}>
              <span
                className={styles.megaViewAll}
                onClick={() => Catagory("all")}
              >
                View All Products →
              </span>
            </div>
          </div>
        ),
      },
    ],
  });

  // Listen for edit search event from SearchData page
  useEffect(() => {
    const handler = () => {
      const lastSearch = localStorage.getItem("tdc_last_search") || "";
      setSearchType(lastSearch);
      setMobileSearchOpen(true);
    };
    window.addEventListener("openMobileSearch", handler);
    return () => window.removeEventListener("openMobileSearch", handler);
  }, []);

  const [text, setText] = useState("");
  useEffect(() => {
    async function getExistingMarquee() {
      try {
        const response = await axios.get(apiUrl.GET_MARQUEE);
        setText(response.data.marquee.text);
      } catch (error) {
        console.error(error);
      }
    }

    getExistingMarquee();
  }, [userToken]);

  return (
    <div className={styles.headerRoot}>
      <div className={styles.offerWrap}>
        <Marquee className={styles.offer} speed={40} gradient={false}>
          <span className={styles.offerItem}>
            <span className={styles.offerDiamond}>&#9670;</span>
            {text}
          </span>
          <span className={styles.offerItem}>
            <span className={styles.offerDiamond}>&#9670;</span>
            {text}
          </span>
        </Marquee>
      </div>

      {/* Mobile Header */}
      <div className={styles.mobileHeader}>
        <div className={styles.mobileHeaderInner}>
          <div className={styles.mobileHeaderSide}>
            <button
              className={styles.mobileIconBtn}
              onClick={openMobileMenu}
              aria-label="Menu"
            >
              <MenuOutlined />
            </button>
            <button
              className={styles.mobileIconBtn}
              onClick={() => setMobileSearchOpen((prev) => !prev)}
              aria-label="Search"
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>

          <div className={styles.mobileHeaderLogo} onClick={handlesubmit}>
            <span className={styles.mobileHeaderLogoTop}>{branding.whitelabel ? branding.name : "The Diamond"}</span>
            <span className={styles.mobileHeaderLogoBottom}>{branding.whitelabel ? "" : "Counter"}</span>
          </div>

          <div className={`${styles.mobileHeaderSide} ${styles.mobileHeaderSideRight}`}>
            <button
              className={styles.mobileIconBtn}
              onClick={() => (token ? navigate(routes.accountUrl) : navigate(routes.loginUrl))}
              aria-label="Account"
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            <button
              className={styles.mobileIconBtn}
              onClick={handleCart}
              aria-label="Basket"
            >
              <Badge
                style={{ backgroundColor: cartlist?.Count > 0 ? "#C5A028" : "#111" }}
                size="small"
                count={cartlist?.Count > 0 ? cartlist?.Count : 0}
                offset={[2, -2]}
              >
                <ShoppingCartOutlined style={{ fontSize: 21, color: "#111" }} />
              </Badge>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className={styles.topRowWrap}>
        <div className="site-container">
          <Row
            className={`${styles.headermain} ${styles.topHeaderRow}`}
            justify="space-between"
            align="bottom"
          >
        <Col
          md={8}
          lg={8}
          xl={8}
          xxl={8}
          className={styles.headerlogo}
          onClick={handlesubmit}
        >
          <Image preview={false} src={branding.logo} alt={branding.name} />
        </Col>

        <Col
          md={11}
          lg={11}
          xl={11}
          xxl={11}
          className={styles.desktopSearchCol}
        >
          <div className={styles.headericon}>
            <div
              onClick={() => setDesktopSearchOpen((prev) => !prev)}
              className={styles.badge}
            >
              <SearchOutlined
                style={{
                  color: "#000",
                  fontSize: 15,
                  paddingRight: 5,
                }}
              />
              <p style={{ fontSize: 12 }}>Search</p>
            </div>

            <div style={{ padding: 10 }}></div>

            {token ? (
              <>
                <div
                  onClick={() => navigate(routes.accountUrl)}
                  className={styles.badge}
                  style={{ cursor: "pointer" }}
                >
                  <OrderedListOutlined
                    style={{
                      color: "#000",
                      fontSize: 15,
                      paddingRight: 5,
                    }}
                  />
                  <p style={{ fontSize: 12 }}>My Account</p>
                </div>

                <div
                  style={{
                    padding: 10,
                  }}
                ></div>
              </>
            ) : null}

            <div onClick={Like} className={styles.badge}>
              <HeartOutlined
                style={{
                  color: "#000",
                  fontSize: 15,
                  paddingRight: 5,
                }}
              />
              <p style={{ fontSize: 12 }}>My Wishlist</p>
              <p className={wishlist > 0 ? styles.badgCounterActive : styles.badgCounter}>
                {wishlist > 0 ? wishlist : 0}
              </p>
            </div>

            <div
              style={{
                padding: 10,
              }}
            ></div>

            <div onClick={handleCart} className={styles.badge}>
              <ShoppingCartOutlined
                style={{
                  color: "#000",
                  fontSize: 15,
                  paddingRight: 5,
                }}
              />
              <p style={{ fontSize: 12 }}>Basket</p>
              <p className={cartlist?.Count > 0 ? styles.badgCounterActive : styles.badgCounter}>
                {cartlist?.Count > 0 ? cartlist?.Count : 0}
              </p>
            </div>

            <div
              style={{
                padding: 10,
              }}
            ></div>

            {token ? (
              <>
                <div
                  style={{ color: "red", cursor: "pointer", fontSize: 12 }}
                  onClick={handleReset}
                >
                  Logout{" "}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{ color: "black", cursor: "pointer", fontSize: 12 }}
                  onClick={() => navigate(routes.loginUrl)}
                >
                  LOGIN
                </div>
              </>
            )}
          </div>
        </Col>

          </Row>

        </div>
      </div>

      {desktopSearchOpen && (
        <div className={styles.desktopSearchBar}>
          <div className="site-container">
            <div className={styles.desktopSearchInner}>
              <Input
                size="large"
                placeholder="Search products..."
                className={styles.desktopSearchInput}
                suffix={<SearchOutlined />}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchType(val);
                  clearTimeout(searchDebounceTimer);
                  if (val && val.trim().length >= 2) {
                    localStorage.setItem("tdc_last_search", val);
                    searchDebounceTimer = setTimeout(() => {
                      dispatch(getSearchProductApi(val, userId || 0, goToSearchResults()));
                    }, 350);
                  }
                }}
                autoFocus
              />
              <CloseOutlined
                className={styles.desktopSearchClose}
                onClick={() => setDesktopSearchOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.navRowWrap}>
        <div className="site-container">
          <Row className={`${styles.headermain} ${styles.navRow}`}>
            <Col xs={24} md={24} lg={24} xl={24} xxl={24}>
              <Row justify="start" align={"middle"} gutter={25}>
            {categoryLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6, 7].map((_, i) => (
                  <Col key={i}>
                    <Skeleton.Button active size="small" />
                  </Col>
                ))}
              </>
            ) : (
              <>
                <Col>
                  <div
                    className={`${styles.navItem} ${location.pathname === routes.homepageUrl ? styles.activeNav : ""}`}
                    onClick={() => navigate(routes.homepageUrl)}
                  >
                    Home
                  </div>
                </Col>

                {showProductRangeMenu && sortedCategories?.map((item, i) => (
                  <Col key={i}>
                    <Dropdown
                      key={item._id}
                      menu={megaMenu(item)}
                      trigger={["hover"]}
                      open={openMenu === "pr"}
                      onOpenChange={(o) => setOpenMenu(o ? "pr" : null)}
                      overlayClassName={styles.megaDropdown}
                    >
                      <div className={styles.navItem}>
                        Product Range
                      </div>
                    </Dropdown>
                  </Col>
                ))}

                {/* Branded Jewellery Mega Menu (visibility controlled per retailer) */}
                {showBrandedMenu && visibleBranded.length > 0 && (
                  <Col>
                    <Dropdown
                      menu={buildMegaMenu(visibleBranded)}
                      trigger={["hover"]}
                      open={openMenu === "br"}
                      onOpenChange={(o) => setOpenMenu(o ? "br" : null)}
                      overlayClassName={styles.megaDropdown}
                    >
                      <div className={styles.navItem}>
                        Branded Jewellery
                      </div>
                    </Dropdown>
                  </Col>
                )}

                {/* Watches — real mega menu when enabled + built, else coming-soon fallback */}
                {watchesMenuReady ? (
                  <Col>
                    <Dropdown
                      menu={buildMegaMenu(visibleWatches)}
                      trigger={["hover"]}
                      open={openMenu === "w"}
                      onOpenChange={(o) => setOpenMenu(o ? "w" : null)}
                      overlayClassName={styles.megaDropdown}
                    >
                      <div className={styles.navItem}>
                        Watches
                      </div>
                    </Dropdown>
                  </Col>
                ) : (
                  <Col>
                    <div
                      className={styles.navItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        const el = document.getElementById("watches-coming-soon-modal");
                        if (el) el.style.display = "flex";
                      }}
                    >
                      Watches
                    </div>
                  </Col>
                )}

                <Col>
                  <div
                    className={styles.navItem}
                    onClick={() => navigate(routes.designCadUrl)}
                  >
                    Design & CAD
                  </div>
                </Col>

                <Col>
                  <div
                    className={styles.navItem}
                    onClick={() => navigate(routes.sellGoldQuoteUrl)}
                  >
                    Sell Your Gold
                  </div>
                </Col>

                <Col>
                  <div
                    className={`${styles.navItem} ${location.pathname === routes.aboutUrl ? styles.activeNav : ""}`}
                    onClick={() => navigate(routes.aboutUrl)}
                  >
                    Our Story
                  </div>
                </Col>

                <Col>
                  <div
                    className={`${styles.navItem} ${location.pathname === routes.contactUrl ? styles.activeNav : ""}`}
                    onClick={() => navigate(routes.contactUrl)}
                  >
                    Contact Us
                  </div>
                </Col>
              </>
            )}
              </Row>
            </Col>
          </Row>
        </div>
      </div>

      <Drawer
        className={styles.mobileMenuDrawer}
        placement="left"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        closeIcon={null}
        width={240}
      >
        <div className={styles.mobileDrawerHeader}>
          <img
            src={branding.logo}
            alt={branding.name}
            className={styles.mobileDrawerHeaderLogo}
          />
          <button
            type="button"
            className={styles.mobileDrawerHeaderClose}
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className={styles.mobileDrawerNav}>

          {showProductRangeMenu && (<>
          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => toggleMobileTop("pr")}
          >
            <span>Product Range</span>
            <RightOutlined
              className={`${styles.mobileNavArrow} ${mobileProductRangeOpen ? styles.mobileNavArrowOpen : ""}`}
            />
          </div>

          {mobileProductRangeOpen && (
            <div className={styles.mobileSubList}>
              {visibleProductRange.map((col) => (
                <div key={col.title}>
                  <div
                    className={styles.mobileNavItem}
                    onClick={() => {
                      if (col.items.length === 0 && col.onTitleClick) {
                        handleMobileNav(col.onTitleClick);
                      } else {
                        setExpandedMobileSection(expandedMobileSection === col.title ? null : col.title);
                      }
                    }}
                  >
                    <span>{col.title}</span>
                    {col.items.length > 0 && (
                      <RightOutlined
                        className={`${styles.mobileNavArrow} ${expandedMobileSection === col.title ? styles.mobileNavArrowOpen : ""}`}
                      />
                    )}
                  </div>
                  {expandedMobileSection === col.title && col.items.length > 0 && (
                    <div className={styles.mobileSubList}>
                      {col.onTitleClick && (
                        <div className={styles.mobileSubItem} style={{ fontWeight: 600, color: "#C5A028" }} onClick={() => handleMobileNav(col.onTitleClick)}>
                          View All {col.title}
                        </div>
                      )}
                      {col.items.map((item, i) => {
                        const label = typeof item === "string" ? item : item.label;
                        const action = typeof item === "object" ? () => handleMobileNav(item.action) : null;
                        const grandKids = typeof item === "object" && Array.isArray(item.children) ? item.children : [];
                        return (
                          <div key={i}>
                            <div className={styles.mobileSubItem} onClick={action || undefined}>
                              {label}
                            </div>
                            {grandKids.map((g, gi) => (
                              <div
                                key={gi}
                                className={styles.mobileSubItem}
                                style={{ paddingLeft: 32, fontSize: 12, color: "#888" }}
                                onClick={() => handleMobileNav(g.action)}
                              >
                                {g.label}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </>)}

          {showBrandedMenu && visibleBranded.length > 0 && (<>
          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => toggleMobileTop("branded")}
          >
            <span>Branded Jewellery</span>
            <RightOutlined
              className={`${styles.mobileNavArrow} ${mobileBrandedOpen ? styles.mobileNavArrowOpen : ""}`}
            />
          </div>

          {mobileBrandedOpen && (
            <div className={styles.mobileSubList}>
              {visibleBranded.map((col) => (
                <div key={col.title}>
                  <div
                    className={styles.mobileNavItem}
                    onClick={() => {
                      if (col.items.length > 0) {
                        setExpandedMobileSection(expandedMobileSection === `branded-${col.title}` ? null : `branded-${col.title}`);
                      } else {
                        col.onTitleClick?.();
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    <span>{col.title}</span>
                    <RightOutlined
                      className={`${styles.mobileNavArrow} ${expandedMobileSection === `branded-${col.title}` ? styles.mobileNavArrowOpen : ""}`}
                    />
                  </div>
                  {expandedMobileSection === `branded-${col.title}` && (
                    <div className={styles.mobileSubList}>
                      {col.onTitleClick && (
                        <div className={styles.mobileSubItem} style={{ fontWeight: 600, color: "#C5A028" }} onClick={() => { col.onTitleClick(); setMobileMenuOpen(false); }}>
                          View All {col.title}
                        </div>
                      )}
                      {col.items.map((item, i) => {
                        const label = typeof item === "string" ? item : item.label;
                        const action = typeof item === "object" ? item.action : null;
                        const grandKids = typeof item === "object" && Array.isArray(item.children) ? item.children : [];
                        return (
                          <div key={i}>
                            <div className={styles.mobileSubItem} onClick={() => { if (action) { action(); setMobileMenuOpen(false); } }}>
                              {label}
                            </div>
                            {grandKids.map((g, gi) => (
                              <div
                                key={gi}
                                className={styles.mobileSubItem}
                                style={{ paddingLeft: 32, fontSize: 12, color: "#888" }}
                                onClick={() => { g.action?.(); setMobileMenuOpen(false); }}
                              >
                                {g.label}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </>)}

          {watchesMenuReady ? (<>
          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => toggleMobileTop("watches")}
          >
            <span>Watches</span>
            <RightOutlined
              className={`${styles.mobileNavArrow} ${mobileWatchesOpen ? styles.mobileNavArrowOpen : ""}`}
            />
          </div>
          {mobileWatchesOpen && (
            <div className={styles.mobileSubList}>
              {visibleWatches.map((col) => (
                <div key={col.title}>
                  <div
                    className={styles.mobileNavItem}
                    onClick={() => {
                      if (col.items.length > 0) {
                        setExpandedMobileSection(expandedMobileSection === `watches-${col.title}` ? null : `watches-${col.title}`);
                      } else {
                        col.onTitleClick?.();
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    <span>{col.title}</span>
                    <RightOutlined
                      className={`${styles.mobileNavArrow} ${expandedMobileSection === `watches-${col.title}` ? styles.mobileNavArrowOpen : ""}`}
                    />
                  </div>
                  {expandedMobileSection === `watches-${col.title}` && (
                    <div className={styles.mobileSubList}>
                      {col.onTitleClick && (
                        <div className={styles.mobileSubItem} style={{ fontWeight: 600, color: "#C5A028" }} onClick={() => { col.onTitleClick(); setMobileMenuOpen(false); }}>
                          View All {col.title}
                        </div>
                      )}
                      {col.items.map((item, i) => {
                        const label = typeof item === "string" ? item : item.label;
                        const action = typeof item === "object" ? item.action : null;
                        const grandKids = typeof item === "object" && Array.isArray(item.children) ? item.children : [];
                        return (
                          <div key={i}>
                            <div className={styles.mobileSubItem} onClick={() => { if (action) { action(); setMobileMenuOpen(false); } }}>
                              {label}
                            </div>
                            {grandKids.map((g, gi) => (
                              <div
                                key={gi}
                                className={styles.mobileSubItem}
                                style={{ paddingLeft: 32, fontSize: 12, color: "#888" }}
                                onClick={() => { g.action?.(); setMobileMenuOpen(false); }}
                              >
                                {g.label}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </>) : (
          <>
          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => {
              setMobileMenuOpen(false);
              setTimeout(() => {
                const el = document.getElementById("watches-coming-soon-modal");
                if (el) el.style.display = "flex";
              }, 300);
            }}
          >
            <span>Watches</span>
            <span style={{ background: "#C5A028", color: "#fff", fontSize: 8, fontWeight: 700, padding: "2px 6px", letterSpacing: 0.5, textTransform: "uppercase" }}>Coming Soon</span>
          </div>
          </>
          )}

          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => handleMobileNav(() => navigate(routes.designCadUrl))}
          >
            <span>Design & CAD</span>
          </div>

          <div className={styles.mobileNavDivider} />
          <div
            className={styles.mobileNavItem}
            onClick={() => handleMobileNav(() => navigate(routes.sellGoldQuoteUrl))}
          >
            <span>Sell Your Gold</span>
          </div>

          <div className={styles.mobileNavDivider} />

          <div
            className={styles.mobileNavItem}
            onClick={() => handleMobileNav(() => navigate(routes.aboutUrl))}
          >
            <span>Our Story</span>
          </div>

          <div className={styles.mobileNavDivider} />

          <div
            className={styles.mobileNavItem}
            onClick={() => handleMobileNav(() => navigate(routes.contactUrl))}
          >
            <span>Contact Us</span>
          </div>
        </div>

      </Drawer>

      {/* Mobile floating quick-access menu */}
      {!mobileMenuOpen && (
        <div className={styles.fab}>
          <div
            className={`${styles.fabBackdrop} ${fabOpen ? styles.fabBackdropOpen : ""}`}
            onClick={() => setFabOpen(false)}
          />
          <div className={`${styles.fabFan} ${fabOpen ? styles.fabFanOpen : ""}`}>
            {[
              { label: "My Account", color: "#5B7C99", icon: <UserOutlined />, onClick: () => (token ? navigate(routes.accountUrl) : navigate(routes.loginUrl)) },
              { label: "Wish List", color: "#C25D74", icon: <HeartOutlined />, onClick: Like },
              { label: "Search", color: "#3FA796", icon: <SearchOutlined />, onClick: () => setMobileSearchOpen(true) },
              { label: "Ring Size Chart", color: "#9B6FB0", onClick: () => setRingChartOpen(true), icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="14.5" r="6.5" />
                  <path d="M8.7 8.2L12 3.5l3.3 4.7" />
                </svg>
              ) },
              { label: "Diamond Size Chart", color: "#4677C0", soon: true, icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 3h14l3 6-10 12L2 9z" />
                </svg>
              ) },
              { label: "Calculators", color: "#D9943B", soon: true, icon: <CalculatorOutlined /> },
            ].map((opt, i, arr) => (
              <button
                key={opt.label}
                type="button"
                className={styles.fabItem}
                style={{ transitionDelay: fabOpen ? `${(arr.length - 1 - i) * 40}ms` : "0ms" }}
                disabled={opt.soon}
                onClick={() => {
                  if (opt.soon) return;
                  setFabOpen(false);
                  opt.onClick();
                }}
              >
                <span className={styles.fabLabel}>
                  {opt.label}
                  {opt.soon && <span className={styles.fabSoon}>Soon</span>}
                </span>
                <span className={styles.fabIcon} style={{ background: opt.color }}>
                  {opt.icon}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.fabToggle}
            onClick={() => setFabOpen((v) => !v)}
            aria-label="Quick menu"
          >
            {fabOpen ? <CloseOutlined /> : <SettingOutlined />}
          </button>
        </div>
      )}

      {/* Mobile search overlay triggered from bottom bar */}
      {mobileSearchOpen && (
        <div className={styles.mobileSearchOverlay}>
          <div className={styles.mobileSearchOverlayInner}>
            <Input
              size="large"
              placeholder="Search products..."
              className={styles.mobileSearchInput}
              value={searchType || ""}
              suffix={<SearchOutlined style={{ cursor: "pointer" }} onClick={() => {
                if (searchType && searchType.trim()) {
                  localStorage.setItem("tdc_last_search", searchType);
                  dispatch(getSearchProductApi(searchType, userId || 0,
                    goToSearchResults({ closeMobileSearch: true })));
                }
              }} />}
              onChange={(e) => setSearchType(e.target.value)}
              onPressEnter={() => {
                if (searchType && searchType.trim()) {
                  localStorage.setItem("tdc_last_search", searchType);
                  dispatch(getSearchProductApi(searchType, userId || 0,
                    goToSearchResults({ closeMobileSearch: true })));
                }
              }}
              autoFocus
            />
            <CloseOutlined
              className={styles.mobileSearchClose}
              onClick={() => setMobileSearchOpen(false)}
            />
          </div>
        </div>
      )}
      {/* Watches Coming Soon Modal */}
      <div
        id="watches-coming-soon-modal"
        onClick={(e) => { if (e.target.id === "watches-coming-soon-modal") e.target.style.display = "none"; }}
        style={{
          display: "none",
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 10000,
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div style={{
          background: "#fff",
          maxWidth: 400,
          width: "100%",
          padding: "40px 32px",
          textAlign: "center",
          position: "relative",
        }}>
          <button
            onClick={() => { document.getElementById("watches-coming-soon-modal").style.display = "none"; }}
            style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#999" }}
          >
            &times;
          </button>
          <div style={{ fontSize: 36, marginBottom: 12 }}>&#9201;</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111", marginBottom: 8, letterSpacing: 1 }}>
            WATCHES
          </h3>
          <div style={{ width: 30, height: 2, background: "#C5A028", margin: "0 auto 16px" }}></div>
          <p style={{ fontSize: 14, color: "#555", lineHeight: 1.7, marginBottom: 4 }}>
            Our curated watch collection is <strong>coming soon</strong>.
          </p>
          <p style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>
            We're partnering with leading brands to bring you an exclusive selection of luxury timepieces. Stay tuned.
          </p>
        </div>
      </div>

      <RingSizeChartModal open={ringChartOpen} onClose={() => setRingChartOpen(false)} />
    </div>
  );
}

export default Header;
